import { JobPostingJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo } from 'react';

import { Match } from '@pflegia/data-access';
import { EmploymentType } from '@pflegia/types/api/candidate/employment-type';
import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import { ConnectionStatus, OfferType } from '@pflegia/types/common/enums';
import { useCanonicalUrl } from '@pflegia/ui/hooks/use-canonical-url';
import { JOB_DETAILS } from '@pflegia/ui/pflegia/constants/routes';
import { getPflegiaDefaultDomainName } from '@pflegia/ui/shared/utils/get-pflegia-default-domain';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import getCanonicalForTransparentPages from '@domains/job-board/utils/get-canonical-for-transparent-pages';
import { JobDetailPageProps } from '@domains/job-detail/types/job-detail-page-props';
import { getDatePosted } from '@domains/job-detail/utils/get-date-posted';
import { mapSeoEmploymentType } from '@domains/job-detail/utils/map-seo-employment-type';

import { useGetJsonldJobStructuredData } from '@hooks/use-get-jsonld-job-structured-data';
import { useGetJsonldJobpositionDescriptionOutput } from '@hooks/use-get-jsonld-jobposition-description-output';
import { useRouterHelper } from '@hooks/use-router-helper';

import { TrackingEventName } from '@shared/application/marketing/events/tracking-event-name';
import { useTracking } from '@shared/application/tracking.service';

const JobDetailsJobPostingJsonLd: React.FC<JobDetailPageProps & { match?: Match }> = ({ match, jobPosition, validThrough }) => {
  const { track } = useTracking();
  const { isAnyJobPage, isJobDetailsPage } = useRouterHelper();
  const canonicalUrl = useCanonicalUrl();
  const route = useRouter();

  const {
    administrativeAreaLevel1,
    administrativeAreaLevel2,
    administrativeAreaLevel3,
    administrativeAreaLevel4,
    administrativeAreaLevel5,
  } = jobPosition?.geoLocation ?? {};

  // @ts-expect-error Conflict with custom vs generated type
  const descriptionLd = useGetJsonldJobpositionDescriptionOutput(jobPosition);
  // @ts-expect-error Conflict with custom vs generated type
  const structuredData = useGetJsonldJobStructuredData(jobPosition);

  useEffect(() => {
    if (match?.connectionStatus === ConnectionStatus.Pending && match?.offerType === OfferType.Push) {
      track(match?.isMachineLearningInvite ? TrackingEventName.ModelPushInviteOpen : TrackingEventName.RegularPushInviteOpen);
    }
  }, [match, track]);

  const shouldPublishOnGoogleJobs = useMemo(() => {
    const hasOrganisation = jobPosition.employer?.organisation;
    const organisationPublishOnGoogleJobs = hasOrganisation ? hasOrganisation.publishOnGoogleJobs : true;
    return jobPosition.publishOnGoogleJobs && jobPosition.employer?.publishOnGoogleJobs && organisationPublishOnGoogleJobs;
  }, [jobPosition]);

  if (!shouldPublishOnGoogleJobs) return null;

  const defaultDomain = getPflegiaDefaultDomainName(jobPosition.employer.sourceDomains as SourceDomain[]);
  const canonical = canonicalUrl({
    flavorDomain: defaultDomain,
    defaultDomain: SourceDomain.PflegiaDe,
    path: isAnyJobPage ? route.asPath : getCanonicalForTransparentPages(route.asPath, JOB_DETAILS),
    removeQueryParams: true,
  });
  const schemaTitle = jobPosition.headline ?? jobPosition.title ?? '';

  return (
    <JobPostingJsonLd
      keyOverride={jobPosition.id}
      id={canonical}
      datePosted={getDatePosted(jobPosition?.canStartDate)}
      description={descriptionLd}
      hiringOrganization={{
        name: jobPosition.employer.name,
        logo: getPublicImageUrl(jobPosition.employer.logoKey),
        sameAs: jobPosition.employer.employerUrl || '',
      }}
      jobLocation={{
        streetAddress: jobPosition?.geoLocation?.streetAddressAndNumber ? jobPosition?.geoLocation?.streetAddressAndNumber : undefined,
        addressLocality: jobPosition?.geoLocation?.city ? jobPosition?.geoLocation?.city : undefined,
        addressRegion:
          administrativeAreaLevel1 ??
          administrativeAreaLevel2 ??
          administrativeAreaLevel3 ??
          administrativeAreaLevel4 ??
          administrativeAreaLevel5 ??
          undefined,
        postalCode: jobPosition?.geoLocation?.postalCode ?? undefined,
        addressCountry: jobPosition?.geoLocation?.country ?? 'Deutschland',
      }}
      title={schemaTitle}
      baseSalary={
        jobPosition.showSalary && jobPosition.employer.showSalary
          ? {
              currency: 'EUR',
              value: [jobPosition.salaryLow ?? 0, jobPosition.salaryHigh ?? 0],
              unitText: 'MONTH',
            }
          : undefined
      }
      employmentType={jobPosition?.employmentTypes?.map(type => mapSeoEmploymentType(type as EmploymentType))}
      applicantLocationRequirements='DE'
      validThrough={validThrough}
      directApply={isJobDetailsPage}
      {...structuredData}
    />
  );
};

export default JobDetailsJobPostingJsonLd;
